import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { PublicBooking } from "@/api/types";
import { Button, tx } from "@/components/ui";
import { PublicShell, reasonText } from "@/public/PublicShell";

type Offer = { status: string; slot_start: string; slot_end: string; expires_at: string; tz: string; store: { slug: string; name: Record<string, string>; address: string | null }; service: { id: string; name: Record<string, string> }; manage_token: string | null };
const fmt = (iso: string, tz: string, lang: string) => new Date(iso).toLocaleString(lang, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: tz });

/** /w/:token — one-tap claim of a waitlist offer. First tap wins. */
export function WaitlistClaim() {
  const { token = "" } = useParams();
  const { t, i18n } = useTranslation();
  const q = useQuery({ queryKey: ["pub-offer", token], queryFn: () => api<Offer>(`/public/waitlist/offers/${token}`), retry: false });
  const claim = useMutation({ mutationFn: () => api<PublicBooking>(`/public/waitlist/offers/${token}/claim`, { method: "POST" }), onSettled: () => q.refetch() });
  if (q.isError) return <PublicShell><p className="rounded-2xl bg-white p-5 shadow text-sm">{t("manage.notFound")}</p></PublicShell>;
  if (!q.data) return <PublicShell><p className="text-gray-500">{t("app.loading")}</p></PublicShell>;
  const o = q.data;
  const done = claim.data;
  return (
    <PublicShell title={t("claim.title")}>
      <section className="rounded-2xl bg-white shadow p-5 space-y-3 text-sm">
        <h2 className="font-semibold">{tx(o.service.name, i18n.language)}</h2>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="font-medium">{fmt(o.slot_start, o.tz, i18n.language)}</div>
          <div className="text-gray-600">{tx(o.store.name, i18n.language)}{o.store.address ? ` · ${o.store.address}` : ""}</div>
        </div>
        {done && (
          <div className="space-y-2">
            <p className="text-green-700 font-medium">{t("claim.done")}</p>
            <div className="flex gap-2"><a className="rounded-lg border px-3 py-1.5" href={`/b/${done.manage_token}`}>{t("claim.manage")}</a><a className="rounded-lg border px-3 py-1.5" href={`/api/v1/public/bookings/${done.manage_token}/ics`}>{t("book.addToCalendar")}</a></div>
          </div>
        )}
        {!done && o.status === "sent" && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">{t("claim.validUntil", { until: fmt(o.expires_at, o.tz, i18n.language) })}</p>
            {claim.isError && <p className="text-red-700">{claim.error instanceof ApiError ? reasonText(t, claim.error.detail) : t("app.error")}</p>}
            <Button type="button" disabled={claim.isPending} onClick={() => claim.mutate()}>{t("claim.button")}</Button>
          </div>
        )}
        {!done && o.status === "claimed" && <div className="space-y-2"><p className="text-green-700">{t("claim.alreadyYours")}</p>{o.manage_token && <a className="inline-block rounded-lg border px-3 py-1.5" href={`/b/${o.manage_token}`}>{t("claim.manage")}</a>}</div>}
        {!done && (o.status === "dead" || o.status === "expired") && <p className="text-amber-700">{t(o.status === "dead" ? "claim.taken" : "claim.expired")}</p>}
      </section>
    </PublicShell>
  );
}
